import * as React from "react"

import { useI18n, t } from "@/lib/i18n"
import { cn } from "@/lib/utils"

/**
 * Elegir un mes, en el idioma de la SOCIEDAD.
 *
 * POR QUE NO ES UN `<input type="month">`. El control nativo se pinta con el
 * idioma del NAVEGADOR, no con el de la aplicacion: sobre una pantalla en
 * español escribia «August 2026», y en la sociedad brasileña habria escrito
 * «August» igual. Eso rompe la regla transversal del producto —el idioma sigue
 * al pais de la sociedad, no al navegador de quien mira— y no hay forma de
 * forzarlo: el navegador no acepta un `lang` para ese widget.
 *
 * Dos `select` ademas se operan mejor. El periodo se cambia constantemente
 * mientras se lee un consolidado, y el calendario nativo obliga a abrir un
 * desplegable con dias que aqui no significan nada.
 *
 * El valor sigue siendo 'YYYY-MM': el contrato con el backend no cambia.
 */

const ANIOS_ATRAS = 4
const ANIOS_ADELANTE = 1

export function SelectorDePeriodo({
  value,
  onChange,
  id = "periodo",
  className,
}: {
  /** 'YYYY-MM'. */
  value: string
  onChange: (v: string) => void
  id?: string
  className?: string
}) {
  const { locale } = useI18n()

  const [anio, mes] = React.useMemo(() => {
    const partes = (value || "").split("-")
    const a = Number(partes[0]) || new Date().getFullYear()
    const m = Number(partes[1]) || new Date().getMonth() + 1
    return [a, m]
  }, [value])

  const meses = React.useMemo(() => {
    // `Intl` con el locale de la sociedad: «agosto» en Perú, «agosto» en
    // México, «agosto» en Brasil es «agosto» en pt-BR — y «August» solo si la
    // sociedad es de un país anglófono, que es cuando corresponde.
    const fmt = new Intl.DateTimeFormat(locale, { month: "long", timeZone: "UTC" })
    return Array.from({ length: 12 }, (_, i) => ({
      valor: i + 1,
      // Mayúscula inicial: `Intl` devuelve minúscula en español y portugués.
      etiqueta: (() => {
        const n = fmt.format(new Date(Date.UTC(2020, i, 1)))
        return n.charAt(0).toUpperCase() + n.slice(1)
      })(),
    }))
  }, [locale])

  const actual = new Date().getFullYear()
  const anios = Array.from(
    { length: ANIOS_ATRAS + ANIOS_ADELANTE + 1 },
    (_, i) => actual - ANIOS_ATRAS + i,
  )

  const clase = cn(
    "h-7 rounded-md border bg-surface px-2 text-xs",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
  )

  function emitir(a: number, m: number) {
    onChange(`${a}-${String(m).padStart(2, "0")}`)
  }

  return (
    <div className={cn("flex items-center gap-1", className)}>
      <select
        id={id}
        className={clase}
        value={mes}
        onChange={(e) => emitir(anio, Number(e.target.value))}
        aria-label={t("Mes")}
      >
        {meses.map((m) => (
          <option key={m.valor} value={m.valor}>
            {m.etiqueta}
          </option>
        ))}
      </select>
      <select
        className={cn(clase, "code")}
        value={anio}
        onChange={(e) => emitir(Number(e.target.value), mes)}
        aria-label={t("Año")}
      >
        {anios.map((a) => (
          <option key={a} value={a}>
            {a}
          </option>
        ))}
      </select>
    </div>
  )
}
